import { UIIcon } from '../config/icons';
import { FTranslationGroup, FTranslation, FTranslationCommit, fTranslationCreate, fTranslationsGet, fTranslationsUpdate } from '../lib/mdl/f-translation';
import { uInputTranslator } from '../lib/mdl/u-translate';
import { canManageTranslations } from '../lib/me/can';
import { UIButton, makeActionButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UITextInput, UINumberInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { uiToast } from '../lib/ui/component/toast';
import { $el } from '../lib/ui/elements';
import { UIScreen } from '../lib/ui/screen';
import { UIViewAddSpec, UIViewSpec } from '../lib/ui/view';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $form } from '../lib/y/form';
import { YLanguage, $i18n, y_language_names } from '../lib/y/i18n';
import { $location } from '../lib/y/location';

interface Entry {
    key: string;
    group?: FTranslationGroup;
    item?: FTranslation;
    children: { [key: string]: Entry };
}

const languages: YLanguage[] = ['en', 'sv', 'de'];

interface ValueRow {
    commit: (commits: FTranslationCommit[]) => void;
}

function makeCategory(all_rows: ValueRow[], group: FTranslationGroup, children: { [key: string]: Entry }, depth: number): UIViewAddSpec {
    const arrow = $el.DIV('r-arrow', $el.ICON(UIIcon.ANGLE_RIGHT));
    const child = $el.DIV('tl-translations-block');
    const title = $el.DIV('r-text', group.title || group.key)
    const header = $el.DIV('tl-translations-category', [
        arrow,
        title,
        xavis_experiments && new UIButton({
            icon: UIIcon.PLUS, classes: 'ui-tint-fg-80 m-no-padding m-left', on_click: () => {
                new AddTranslationDialog(group.key).present().then(() => {
                    $location.reload();
                });
            }
        })
    ]);
    header.style.setProperty('--ui-tree-depth', depth.toString());

    for(const key in children) {
        buildTree(all_rows, child, children[key], depth + 1);
    }

    let expanded = false;
    child.ui_hide();

    const on_click = () => {
        expanded = !expanded;
        if(expanded) {
            child.ui_show();
            header.classList.add('f-expanded');
        } else {
            child.ui_hide();
            header.classList.remove('f-expanded');
        }
    }

    arrow.addEventListener('click', on_click);
    title.addEventListener('click', on_click);

    return [header, child];
}

function makeDictionary(all_rows: ValueRow[], key: string, group: FTranslationGroup, children: { [key: string]: Entry }, depth: number): UIViewAddSpec {
    const child = $el.DIV('tl-translations-block m-dictionary');
    const header = $el.DIV('tl-translations-dictionary', [
        group.title || key,
        xavis_experiments && new UIButton({
            icon: UIIcon.PLUS, classes: 'ui-tint-fg-80 m-no-padding m-left', on_click: () => {
                new AddTranslationDialog(group.key + '/').present().then(() => {
                    $location.reload();
                });
            }
        })
    ]);
    header.style.setProperty('--ui-tree-depth', depth.toString());
    for(const key in children) {
        buildTree(all_rows, child, children[key], depth + 1);
    }

    return [header, child];
}

function makeValue(all_rows: ValueRow[], key: string, item: FTranslation, depth: number): UIViewAddSpec {
    const row = $el.DIV(['tl-translations-value'], $el.DIV('r-key', key));
    row.style.setProperty('--ui-tree-depth', depth.toString());
    let lines: number | undefined;

    if(item.value['en']?.[0]) {
        if(item.value['en'][0].length > 40) {
            lines = Math.max(2, Math.ceil(item.value['en'][0].length / 50));
        }
    }

    if(item.lines > 1) {
        lines = Math.max(item.lines, lines || 1);
    }

    const lang_inputs: { [key in YLanguage]?: UITextInput[] } = {};

    for(const lang of languages) {
        const inputs: UITextInput[] = [];
        for(let i = 0; i < item.size; i++) {
            const r_deco = lang != 'en' ? new UIButton({
                icon: UIIcon.WAND_MAGIC_SPARKLES, classes: 'ui-tint-fg-80 m-no-padding', on_click: () => {
                    uInputTranslator(item.value['en']?.[i] || '', 'en', lang).then(translated => { input.value = translated });
                }
            }) : undefined;
            const input = new UITextInput({ kind: 'k-flat', placeholder: y_language_names[lang], lines, r_deco });
            input.value = item.value[lang]?.[i] || '';
            inputs.push(input);
        }
        row.ui_add($el.DIV('r-fields', inputs));
        lang_inputs[lang] = inputs;
    }

    all_rows.push({
        commit: commits => {
            const commit: FTranslationCommit = { group_key: item.group_key, key: item.key, update: {} };
            let some_changed = false;

            for(const lang of languages) {
                const inputs = lang_inputs[lang]!;
                const new_value: string[] = [];
                let changed = false;

                for(let i = 0; i < item.size; i++) {
                    const value = inputs[i].value;
                    if(value != item.value[lang]?.[i] || '') {
                        changed = true;
                    }
                    new_value.push(value);
                }

                if(changed) {
                    commit.update[lang] = new_value;
                    some_changed = true;
                }
            }

            if(some_changed) {
                commits.push(commit);
            }
        }
    });

    return row;
}

function buildTree(all_rows: ValueRow[], parent: HTMLElement, entry: Entry, depth: number) {
    if(entry.group) {
        if(entry.group.is_dictionary)
            parent.ui_add(makeDictionary(all_rows, entry.key, entry.group, entry.children, depth));
        else
            parent.ui_add(makeCategory(all_rows, entry.group, entry.children, depth));
    } else if(entry.item) {
        parent.ui_add(makeValue(all_rows, entry.key, entry.item, depth));
    }
}

class AddTranslationDialog extends UIDialog {
    constructor(group_value?: string) {
        super({ classes: 'm-600 m-mobile-fs' });

        const group_key = new UITextInput({ required: true });
        const key = new UITextInput({ required: true });
        const size = new UINumberInput({ required: true, min: 1 });
        const lines = new UINumberInput({ required: true, min: 1 });
        const title = new UITextInput();
        const hint = new UITextInput();
        const english = new UITextInput();

        const form = $form({ group_key, key, size, lines, title, hint });

        size.value = 1;
        lines.value = 1;
        if(group_value) {
            group_key.value = group_value;
        }

        const buttons = $el.DIV('l-button-row m-right', [
            new UIButton({
                title: $i18n.tl.b_cancel, classes: 'm-hollow', on_click: () => {
                    this.dismiss(null);
                }
            }),
            new UIButton({
                title: "Create", classes: 'ui-tint-reverse', on_click: () => {
                    if(!form.validate()) return;
                    const value = form.value;
                    value.kind = 'value';
                    value.value = { en: [english.value, ...new Array(value.size - 1).fill('')] };

                    return fTranslationCreate(value).then(() => {
                        this.dismiss(true);
                    });
                }
            })
        ]);

        this.content.ui_add($el.DIV('w-block l-form tl-producer-form m-compact', [
            new UILabeled(group_key, { above: 'Group' }),
            new UILabeled(key, { above: 'Key' }),
            $el.DIV('l-input-row', [
                new UILabeled(size, { above: 'Size' }),
                new UILabeled(lines, { above: 'Lines' })
            ]),
            new UILabeled(title, { above: 'Title' }),
            new UILabeled(hint, { above: 'Hint' }),
            new UILabeled(english, { above: 'English value' }),
            buttons
        ]));
    }
}

export class AdminTranslationsScreen extends UIScreen {
    constructor() {
        super();

        this.title = 'Translations';
    }

    protected override async fillContent(content: UIViewSpec) {
        const response = await fTranslationsGet();

        response.groups.sort((a, b) => a.key.localeCompare(b.key));
        response.items.sort((a, b) => a.key.localeCompare(b.key));

        const roots: { [key: string]: Entry } = {};

        for(const group of response.groups) {
            const split = group.key.split('/');

            let error = false;
            let parent = roots;

            for(let i = 0; i < split.length - 1; i++) {
                const part = split[i];
                parent = parent[part]?.children;
                if(!parent) {
                    $log('app', 'Missing translation category', part, group);
                    error = true;
                    break;
                }
            }

            if(error) continue;
            parent[split[split.length - 1]] = { group, key: split[split.length - 1], children: {} };
        }

        for(const item of response.items) {
            let error = false;
            let parent = roots;

            for(const part of item.group_key.split('/')) {
                parent = parent[part]?.children;
                if(!parent) {
                    $log('app', 'Missing translation category', part, item);
                    error = true;
                    break;
                }
            }

            if(error) continue;

            parent[item.key] = { item, key: item.key, children: {} };
        }

        $log('app', 'roots', roots);

        const grid = $el.DIV('tl-translations-grid');

        const all_rows: ValueRow[] = [];

        for(const key in roots) {
            buildTree(all_rows, grid, roots[key], 0);
        }

        content.ui_add($el.SECTION('', [
            $el.DIV('l-h-scroll w-block b', grid)
        ]));

        if(canManageTranslations()) {
            this.ui_add(makeActionButton(UIIcon.THUMBS_UP, "Save", () => {
                const commits: FTranslationCommit[] = [];

                for(const row of all_rows) {
                    row.commit(commits);
                }

                $log('app', 'Translation commits', commits);

                fTranslationsUpdate(commits).then(response => {
                    $log('app', 'Translations updated', response);
                    uiToast('Saved');
                    $location.reload();
                });
            }));
        }

        if(xavis_experiments) {
            this.ui_add(makeActionButton(UIIcon.PLUS, "Add", () => {
                new AddTranslationDialog().present().then(() => {
                    $location.reload();
                });
            }, 'm-left'));
        }
    }
}
